// Shared report-to-recommendation projection. Keep live provenance and ratings gates here.
import { baselineIsSecureScan365 } from "../regressionProvenance";
import { ratingFieldsForRecommendation } from "../findingRatings";

export const normalizeSettingRatings = (setting = {}) => ratingFieldsForRecommendation(setting);

export function projectReportRecommendations(report, scans) {
  if (!report?.scored_settings?.length) {
    return [];
  }
  // Gate for regression UI: only trust regressed_from_passed when the
  // prior scan actually still exists (see the field comment below).
  const hasScanHistory = Array.isArray(scans) && scans.length >= 2;
  // Baseline provenance requires matching stored and publication predecessor
  // identities and an eligible SecureScan365 scan-history entry. A neighboring
  // timestamp is not a fallback; missing or ineligible identity fails closed.
  const baselineOk = baselineIsSecureScan365(report, scans);
  return report.scored_settings.map((s) => {
    let sev = (s.severity || "").toLowerCase() || "medium";
    let status = "open";
    if (s.result === "Passed") status = "implemented";
    else if (s.result === "Failed") status = "open";
    // Partial derivation: backend awarded some but not all points
    if (typeof s.earned === "number" && typeof s.max_possible === "number"
        && s.earned > 0 && s.earned < s.max_possible) {
      status = "partial";
    }
    // Derived assessment outcome (separate from workflow `status` above).
    // scored_status is checked BEFORE result: an advisory/N-A setting can
    // have a "Failed" result underneath and must still resolve accordingly.
    let verdict;
    if (s.scored_status === "advisory") verdict = "advisory";
    else if (s.scored_status === "not_applicable") verdict = "not_assessed";
    else if (s.scored_status === "evaluation_failed") verdict = "not_assessed";
    else if (s.result === "Skipped" || s.result === "Error" || s.result === "NotRun") verdict = "not_assessed";
    else if (s.result === "Passed") verdict = "pass";
    else if (s.result === "Failed") verdict = "fail";
    else verdict = "not_assessed";
    // "New" determination is DISABLED for now: force false.
    // Disabled until ss_DateAdded from Gold Copy flows through the scoring
    // pipeline — see Gold Copy newness work. first_identified is scan-date,
    // not rubric-add-date, so it tags everything. Restore the 90-day logic
    // below once the real source is wired.
    let isNew = false;
    // if (s.first_identified) {
    //   const fi = new Date(s.first_identified);
    //   if (!Number.isNaN(fi.getTime())) {
    //     isNew = (Date.now() - fi.getTime()) <= 90 * 24 * 60 * 60 * 1000;
    //   }
    // }
    return {
      id:                    s.setting_id,
      name:                  s.setting_name,
      cat:                   s.workload || "Other",
      category:              s.category || null,
      license:               s.required_license || null,
      sev:                   sev,
      status:                status,
      scored_status:         s.scored_status,
      result:                s.result,
      skip_reason:           s.skip_reason,
      verdict:               verdict,
      isNew:                 isNew,
      qtr:                   "",  // deprecated per May 5 decision
      first_identified:      s.first_identified || null,
      recommended_value:     s.recommended_value || null,
      actual_value:          s.actual_value || null,  // Backend Phase A1 — clean tenant observed value
      // Backend Phase D — true when this setting regressed from the prior
      // scan. Gated on live scan history >= 2: regressed_from_passed is a
      // scoring-time snapshot and survives deletion of the prior scan
      // (83-customer ghost audit, July 6 2026) — without a surviving prior
      // scan the badge would assert a regression vs a scan we deliberately
      // retired. Single gate point: the KPI card, Recs filter/badges, and
      // counts all read rec.regressed. Not Assessed rows never show
      // regression signals (2026-07-28 decision) — gated here at the source.
      // ADR-0083 weight gate: only score-affecting rows (max_possible > 0)
      // carry the regression signal. Zero-weight rows keep their honest
      // regressed_from_passed flag in the report data and still show as
      // failing / severity-filterable — they just never badge or count.
      // Deliberately retroactive at render: clears informational noise from
      // currently-published reports too. ExecutiveReport and FindingsExport
      // read the raw flag and apply this same gate themselves.
      // ADR-0084 provenance gate: the signal additionally requires the
      // report envelope to say this scan was produced by SecureScan365
      // (collection_method === "securescan365"). Absent (every pre-ADR-0084
      // snapshot) or anything else hides it — fail closed: regressions
      // computed against Patriot-editable Excel-import baselines are sheet
      // edits, not posture changes. Also deliberately retroactive at
      // render — clears the false badges on already-published Excel-era
      // reports without touching an immutable snapshot, at the accepted
      // cost of hiding pre-stamp SecureScan365 regressions until the next
      // scan re-stamps (2026-08-04 decision).
      // ADR-0084 amendment (2026-08-08): the BASELINE must be SecureScan365
      // too — pre-deploy blobs carry flags computed against Excel-import
      // baselines, and the side-car injection re-satisfied the current-side
      // gate on them. Baseline provenance requires an exact predecessor
      // scored_path/sha256 match in the eligible scan-history list, with
      // securescan365 collection_method on report, comparison, and scan.
      // No match fails closed (regressionProvenance.js).
      regressed:             hasScanHistory && !!s.regressed_from_passed && verdict !== "not_assessed"
                               && (typeof s.max_possible === "number" ? s.max_possible : 0) > 0
                               && report?.collection_method === "securescan365"
                               && baselineOk,
      // NOTE: scored blobs carry changed_since_prior (ADR-0083 B, scoring-
      // time stamping — still live and accumulating). The UI deliberately
      // does NOT read it: the CHANGED badge was pulled 2026-08-03 pending
      // the Gold Copy QA sweep and a decision on which "changed" definition
      // the label carries — see the ADR-0083 UI-pull amendment before
      // re-adding anything here.
      max_possible:          typeof s.max_possible === "number" ? s.max_possible : 0,  // Phase E — for category donut math
      earned:                typeof s.earned === "number" ? s.earned : 0,              // Phase E — for category donut math
      result_text:           s.result_text || null,
      error_detail:          s.error_detail || null,
      ...normalizeSettingRatings(s),
      patriot_advisor_value: s.patriot_advisor_value || null,
      audit_recommendation:  s.audit_recommendation || null,
      remediation_steps:     Array.isArray(s.remediation_steps) ? s.remediation_steps : [],
      remediation_url:       s.remediation_url || null,
      help_url:              s.help_url || null,
      default_value:         s.default_value || null,  // Backend will populate from Gold Copy
      last_reviewed:         report.executed_at || null,
    };
  });
}
